import React, { createContext, useContext, useRef, useCallback, useEffect } from "react";

/**
 * Context for managing exercise drag-and-drop across workouts
 * with auto-scroll support at edges
 *
 * Compatible with both react-dnd and @dnd-kit
 */

const ExerciseDragContext = createContext(null);

// Drag type constant for exercises (legacy - for react-dnd compatibility)
export const EXERCISE_DRAG_TYPE = "EXERCISE_CARD";

// Auto-scroll configuration
// Optimized for better UX: activation zone closer to edges, faster scrolling, responsive transitions
const AUTO_SCROLL_CONFIG = {
  edgeThreshold: 100, // px from edge to trigger scroll (closer to edges for more precise control)
  maxScrollSpeed: 15, // max px per frame (faster scrolling for better responsiveness)
  minScrollSpeed: 2, // min px per frame (minimum speed when just entering edge zone)
  scrollEasing: 0.4, // Easing factor for speed transitions (0-1, higher = more responsive)
  positionUpdateThrottle: 8, // ms between position updates (faster updates for smoother tracking)
  // Progressive speed zones for smoother acceleration
  speedZones: {
    fast: 30, // Within 30px of edge = max speed
    medium: 60, // 30-60px = medium speed
    slow: 100, // 60-100px = slow speed
  },
};

export const ExerciseDragProvider = ({ children }) => {
  // Refs for scroll containers
  const horizontalScrollRef = useRef(null);
  const verticalScrollRef = useRef(null);

  // Cached vertical scroll container (found dynamically)
  const cachedVerticalScrollContainerRef = useRef(null);

  // Animation frame ref for auto-scroll
  const scrollAnimationRef = useRef(null);

  // Track current drag state
  const isDraggingRef = useRef(false);
  const lastClientPositionRef = useRef({ x: 0, y: 0 });
  const lastPositionUpdateTimeRef = useRef(0);

  // Cached scroll element references and their bounds (to avoid layout thrashing)
  const cachedScrollElementsRef = useRef({
    horizontal: { element: null, bounds: null, lastBoundsUpdate: 0 },
    vertical: { element: null, bounds: null, lastBoundsUpdate: 0 },
  });

  // Current scroll speeds (for smooth transitions)
  const currentScrollSpeedsRef = useRef({
    horizontal: 0,
    vertical: 0,
  });

  // Active drag item state (for DragOverlay and components)
  const activeDragItemRef = useRef(null);

  // Bounds cache TTL (update bounds every 100ms to avoid layout thrashing)
  const BOUNDS_CACHE_TTL = 100;

  /**
   * Calculate target scroll speed based on distance from edge.
   * Uses progressive speed zones for smoother, more intuitive acceleration.
   */
  const calculateTargetScrollSpeed = useCallback((distanceFromEdge) => {
    const { edgeThreshold, maxScrollSpeed, minScrollSpeed, speedZones } =
      AUTO_SCROLL_CONFIG;

    if (distanceFromEdge >= edgeThreshold) return 0;

    // Progressive speed zones for smoother acceleration
    if (distanceFromEdge <= speedZones.fast) {
      // Fast zone: max speed
      return maxScrollSpeed;
    } else if (distanceFromEdge <= speedZones.medium) {
      // Medium zone: interpolate between max and medium speed
      const zoneProgress = (distanceFromEdge - speedZones.fast) / (speedZones.medium - speedZones.fast);
      const mediumSpeed = minScrollSpeed + (maxScrollSpeed - minScrollSpeed) * 0.6;
      return maxScrollSpeed - (maxScrollSpeed - mediumSpeed) * zoneProgress;
    } else {
      // Slow zone: interpolate between medium and min speed
      const zoneProgress = (distanceFromEdge - speedZones.medium) / (speedZones.slow - speedZones.medium);
      const mediumSpeed = minScrollSpeed + (maxScrollSpeed - minScrollSpeed) * 0.6;
      return mediumSpeed - (mediumSpeed - minScrollSpeed) * zoneProgress;
    }
  }, []);

  /**
   * Smoothly transition scroll speed to target using easing.
   * More responsive transitions for better UX.
   */
  const getSmoothScrollSpeed = useCallback(
    (targetSpeed, currentSpeed, axis) => {
      const { scrollEasing } = AUTO_SCROLL_CONFIG;

      // If target is 0, decay faster for immediate stop
      if (targetSpeed === 0) {
        return currentSpeed * 0.3; // Decay quickly when leaving edge zone
      }

      // Smoothly interpolate to target speed with more responsive easing
      const diff = targetSpeed - currentSpeed;
      const newSpeed = currentSpeed + diff * scrollEasing;
      
      // Ensure we don't overshoot the target
      if ((diff > 0 && newSpeed > targetSpeed) || (diff < 0 && newSpeed < targetSpeed)) {
        return targetSpeed;
      }
      
      return newSpeed;
    },
    [],
  );

  /**
   * Get the actual scrollable DOM element from an RN Web ref
   */
  const getScrollableElement = useCallback((ref) => {
    if (!ref) return null;

    // Direct DOM element (most common case)
    if (
      ref.scrollLeft !== undefined &&
      typeof ref.getBoundingClientRect === "function"
    ) {
      return ref;
    }

    // React Native Web ScrollView: try getScrollableNode() first (most reliable)
    if (typeof ref.getScrollableNode === "function") {
      const scrollableNode = ref.getScrollableNode();
      if (scrollableNode && typeof scrollableNode.getBoundingClientRect === "function") {
        return scrollableNode;
      }
    }

    // Animated.ScrollView._component
    if (
      ref._component &&
      typeof ref._component.getBoundingClientRect === "function"
    ) {
      return ref._component;
    }

    // Try getNode() for older React Native Web
    if (typeof ref.getNode === "function") {
      const node = ref.getNode();
      if (node && typeof node.getBoundingClientRect === "function") {
        return node;
      }
    }

    // Try to find scrollable child element
    if (ref.children && ref.children.length > 0) {
      // Look for first child that has scroll properties
      for (let i = 0; i < ref.children.length; i++) {
        const child = ref.children[i];
        if (child && (child.scrollLeft !== undefined || child.scrollTop !== undefined)) {
          return child;
        }
      }
    }

    // Last resort: return ref as-is (might work if it's already a DOM element)
    return ref;
  }, []);

  /**
   * Get or update cached scroll element bounds.
   * Caches bounds to avoid layout thrashing from repeated getBoundingClientRect calls.
   */
  const getCachedBounds = useCallback((element, cacheKey) => {
    if (!element) return null;

    const cache = cachedScrollElementsRef.current[cacheKey];
    const now = Date.now();

    // Return cached bounds if still valid
    if (
      cache.element === element &&
      cache.bounds &&
      now - cache.lastBoundsUpdate < BOUNDS_CACHE_TTL
    ) {
      return cache.bounds;
    }

    // Update cache
    try {
      const bounds = element.getBoundingClientRect();
      cachedScrollElementsRef.current[cacheKey] = {
        element,
        bounds,
        lastBoundsUpdate: now,
      };
      return bounds;
    } catch (e) {
      return null;
    }
  }, []);

  /**
   * Auto-scroll animation loop with smooth, cached scrolling.
   * Uses requestAnimationFrame for 60fps smooth scrolling.
   */
  const runAutoScroll = useCallback(() => {
    if (!isDraggingRef.current) {
      scrollAnimationRef.current = null;
      // Reset scroll speeds when stopping
      currentScrollSpeedsRef.current = { horizontal: 0, vertical: 0 };
      return;
    }

    const { x, y } = lastClientPositionRef.current;
    const { edgeThreshold } = AUTO_SCROLL_CONFIG;

    // Horizontal scroll (for week columns)
    const hScrollEl = getScrollableElement(horizontalScrollRef.current);
    if (hScrollEl) {
      try {
        const hRect = getCachedBounds(hScrollEl, "horizontal");
        if (!hRect) {
          // Schedule next frame and continue
          scrollAnimationRef.current = requestAnimationFrame(runAutoScroll);
          return;
        }

        // Check if element is actually scrollable horizontally
        const canScrollHorizontally = hScrollEl.scrollWidth > hScrollEl.clientWidth;
        if (!canScrollHorizontally) {
          // Can't scroll, reset speed and continue
          currentScrollSpeedsRef.current.horizontal = 0;
        } else {
          let targetSpeed = 0;

          // Check left edge
          const distFromLeft = x - hRect.left;
          if (distFromLeft < edgeThreshold && distFromLeft >= 0) {
            targetSpeed = -calculateTargetScrollSpeed(distFromLeft);
          }

          // Check right edge
          if (targetSpeed === 0) {
            const distFromRight = hRect.right - x;
            if (distFromRight < edgeThreshold && distFromRight >= 0) {
              targetSpeed = calculateTargetScrollSpeed(distFromRight);
            }
          }

          // Smoothly transition to target speed
          const currentSpeed = currentScrollSpeedsRef.current.horizontal;
          const smoothSpeed = getSmoothScrollSpeed(
            targetSpeed,
            currentSpeed,
            "horizontal",
          );
          currentScrollSpeedsRef.current.horizontal = smoothSpeed;

          // Apply scroll if speed is significant
          if (Math.abs(smoothSpeed) > 0.1) {
            if (typeof hScrollEl.scrollLeft === "number") {
              const currentScrollLeft = hScrollEl.scrollLeft;
              const newScrollLeft = currentScrollLeft + smoothSpeed;
              const maxScroll = Math.max(0, hScrollEl.scrollWidth - hRect.width);
              const clampedScrollLeft = Math.max(0, Math.min(maxScroll, newScrollLeft));
              
              // Only update if it actually changed (avoids unnecessary work)
              if (Math.abs(clampedScrollLeft - currentScrollLeft) > 0.01) {
                hScrollEl.scrollLeft = clampedScrollLeft;
              }
            }
          }
        }
      } catch (e) {
        console.warn('[ExerciseDragContext] Horizontal scroll error:', e);
        // Reset speed on error
        currentScrollSpeedsRef.current.horizontal = 0;
      }
    } else {
      // No horizontal scroll container, reset speed
      currentScrollSpeedsRef.current.horizontal = 0;
    }

    // Vertical scroll (for page)
    if (typeof window !== "undefined" && typeof document !== "undefined") {
      const windowHeight = window.innerHeight;

      // Find vertical scroll container (with caching)
      let scrollEl = cachedVerticalScrollContainerRef.current;

      // Validate cached container
      if (
        !scrollEl ||
        !document.contains(scrollEl) ||
        scrollEl.scrollHeight <= scrollEl.clientHeight
      ) {
        // Find new container
        if (verticalScrollRef.current) {
          scrollEl = getScrollableElement(verticalScrollRef.current);
          if (scrollEl && scrollEl.scrollHeight > scrollEl.clientHeight) {
            cachedVerticalScrollContainerRef.current = scrollEl;
          } else {
            scrollEl = null;
          }
        }

        // Fallback: find from element at pointer position
        if (!scrollEl) {
          const elementAtPoint = document.elementFromPoint(x, y);
          if (elementAtPoint) {
            let current = elementAtPoint;
            while (
              current &&
              current !== document.body &&
              current !== document.documentElement
            ) {
              const style = window.getComputedStyle(current);
              if (
                (style.overflowY === "auto" || style.overflowY === "scroll") &&
                current.scrollHeight > current.clientHeight
              ) {
                scrollEl = current;
                cachedVerticalScrollContainerRef.current = scrollEl;
                break;
              }
              current = current.parentElement;
            }
          }
        }

        // Last resort: check common patterns
        if (!scrollEl) {
          const candidates = [
            document.querySelector("[data-vertical-scroll-container]"),
            document.scrollingElement,
            document.documentElement,
            document.body,
          ];
          for (const el of candidates) {
            if (el) {
              // Check if element is scrollable
              const isScrollable = el.scrollHeight > el.clientHeight;
              // Also check computed style for overflow
              const style = window.getComputedStyle(el);
              const hasOverflow = style.overflowY === "auto" || style.overflowY === "scroll" || style.overflow === "auto" || style.overflow === "scroll";
              
              if (isScrollable || hasOverflow) {
                scrollEl = el;
                cachedVerticalScrollContainerRef.current = scrollEl;
                break;
              }
            }
          }
        }
      }

      if (scrollEl) {
        try {
          const vRect = getCachedBounds(scrollEl, "vertical");
          if (vRect) {
            let targetSpeed = 0;

            // Use scroll container bounds for more accurate edge detection
            const containerTop = vRect.top;
            const containerBottom = vRect.bottom;
            const containerHeight = vRect.height;

            // Check top edge (relative to scroll container)
            const distFromTop = y - containerTop;
            if (distFromTop < edgeThreshold && distFromTop >= 0 && y >= containerTop) {
              targetSpeed = -calculateTargetScrollSpeed(distFromTop);
            }

            // Check bottom edge (relative to scroll container)
            if (targetSpeed === 0) {
              const distFromBottom = containerBottom - y;
              if (distFromBottom < edgeThreshold && distFromBottom >= 0 && y <= containerBottom) {
                targetSpeed = calculateTargetScrollSpeed(distFromBottom);
              }
            }
            
            // Fallback: also check window edges if pointer is outside container bounds
            // This handles cases where the drag preview extends beyond the container
            if (targetSpeed === 0) {
              // Top edge (window)
              const windowDistFromTop = y;
              if (windowDistFromTop < edgeThreshold && windowDistFromTop >= 0) {
                targetSpeed = -calculateTargetScrollSpeed(windowDistFromTop);
              }
              
              // Bottom edge (window)
              if (targetSpeed === 0) {
                const windowDistFromBottom = windowHeight - y;
                if (windowDistFromBottom < edgeThreshold && windowDistFromBottom >= 0) {
                  targetSpeed = calculateTargetScrollSpeed(windowDistFromBottom);
                }
              }
            }

            // Smoothly transition to target speed
            const currentSpeed = currentScrollSpeedsRef.current.vertical;
            const smoothSpeed = getSmoothScrollSpeed(
              targetSpeed,
              currentSpeed,
              "vertical",
            );
            currentScrollSpeedsRef.current.vertical = smoothSpeed;

            // Apply scroll if speed is significant
            if (Math.abs(smoothSpeed) > 0.1) {
              const currentScrollTop = scrollEl.scrollTop;
              const newScrollTop = currentScrollTop + smoothSpeed;
              const maxScroll = Math.max(
                0,
                scrollEl.scrollHeight - vRect.height,
              );
              const clampedScrollTop = Math.max(0, Math.min(maxScroll, newScrollTop));
              
              // Only update if it actually changed (avoids unnecessary work)
              if (Math.abs(clampedScrollTop - currentScrollTop) > 0.01) {
                scrollEl.scrollTop = clampedScrollTop;
              }
            }
          }
        } catch (e) {
          // Silently ignore scroll errors
        }
      }
    }

    // Schedule next frame (only if still dragging)
    if (isDraggingRef.current) {
      scrollAnimationRef.current = requestAnimationFrame(runAutoScroll);
    } else {
      scrollAnimationRef.current = null;
    }
  }, [
    calculateTargetScrollSpeed,
    getSmoothScrollSpeed,
    getScrollableElement,
    getCachedBounds,
  ]);

  // Pointer event handlers for tracking position during dnd-kit drag
  const pointerHandlersRef = useRef({
    onPointerMove: null,
    onTouchMove: null,
  });

  /**
   * Handle pointer/touch move for position tracking during drag
   */
  const handlePointerMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    const clientX = e.clientX;
    const clientY = e.clientY;
    if (clientX !== undefined && clientY !== undefined) {
      lastClientPositionRef.current = { x: clientX, y: clientY };
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    const touch = e.touches?.[0];
    if (touch) {
      lastClientPositionRef.current = { x: touch.clientX, y: touch.clientY };
    }
  }, []);

  /**
   * Start tracking pointer position globally
   * Called when drag starts to enable auto-scroll position tracking
   * Idempotent: safe to call multiple times, won't add duplicate listeners
   */
  const startPointerTracking = useCallback(() => {
    if (typeof document === "undefined") return;
    
    // Don't add listeners if already tracking (idempotent)
    if (pointerHandlersRef.current.onPointerMove) {
      return;
    }

    // Store handlers so we can remove them later
    pointerHandlersRef.current.onPointerMove = handlePointerMove;
    pointerHandlersRef.current.onTouchMove = handleTouchMove;

    // Add global event listeners
    document.addEventListener("pointermove", handlePointerMove, {
      passive: true,
    });
    document.addEventListener("touchmove", handleTouchMove, { passive: true });
    // Also listen for mouse events for broader compatibility
    document.addEventListener("mousemove", handlePointerMove, {
      passive: true,
    });
  }, [handlePointerMove, handleTouchMove]);

  /**
   * Stop tracking pointer position
   * Called when drag ends
   * Idempotent: safe to call multiple times
   */
  const stopPointerTracking = useCallback(() => {
    if (typeof document === "undefined") return;

    const { onPointerMove, onTouchMove } = pointerHandlersRef.current;

    // Remove listeners if they exist
    if (onPointerMove) {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("mousemove", onPointerMove);
    }
    if (onTouchMove) {
      document.removeEventListener("touchmove", onTouchMove);
    }

    // Clear handlers to mark as stopped (allows idempotent calls)
    pointerHandlersRef.current = { onPointerMove: null, onTouchMove: null };
  }, []);

  /**
   * Start auto-scroll when drag begins.
   * Ensures only one animation frame loop is running.
   * Idempotent: safe to call multiple times, won't create duplicate listeners
   */
  const startAutoScroll = useCallback(() => {
    // Don't start if already dragging (idempotent)
    if (isDraggingRef.current) {
      return;
    }

    // Cancel any existing animation frame to prevent duplicates
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current);
      scrollAnimationRef.current = null;
    }

    isDraggingRef.current = true;

    // Reset scroll speeds
    currentScrollSpeedsRef.current = { horizontal: 0, vertical: 0 };

    // Clear cached scroll containers for fresh detection
    cachedVerticalScrollContainerRef.current = null;
    cachedScrollElementsRef.current = {
      horizontal: { element: null, bounds: null, lastBoundsUpdate: 0 },
      vertical: { element: null, bounds: null, lastBoundsUpdate: 0 },
    };

    // Reset position update throttle
    lastPositionUpdateTimeRef.current = 0;

    // Start tracking pointer position for auto-scroll
    startPointerTracking();

    // Start animation loop (only one)
    scrollAnimationRef.current = requestAnimationFrame(runAutoScroll);
  }, [runAutoScroll, startPointerTracking]);

  /**
   * Stop auto-scroll when drag ends.
   * Properly cleans up animation frames and resets state.
   * Idempotent: safe to call multiple times, ensures complete cleanup
   */
  const stopAutoScroll = useCallback(() => {
    // Don't do anything if not dragging (idempotent)
    if (!isDraggingRef.current) {
      return;
    }

    isDraggingRef.current = false;

    // Stop tracking pointer position (removes all listeners)
    stopPointerTracking();

    // Cancel animation frame
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current);
      scrollAnimationRef.current = null;
    }

    // Reset scroll speeds immediately
    currentScrollSpeedsRef.current = { horizontal: 0, vertical: 0 };

    // Clear cached bounds to free memory
    cachedVerticalScrollContainerRef.current = null;
    cachedScrollElementsRef.current = {
      horizontal: { element: null, bounds: null, lastBoundsUpdate: 0 },
      vertical: { element: null, bounds: null, lastBoundsUpdate: 0 },
    };
  }, [stopPointerTracking]);

  /**
   * Update client position during drag with throttling.
   * Throttles position updates to avoid excessive calculations and layout thrashing.
   */
  const updateDragPosition = useCallback((clientX, clientY) => {
    const now = Date.now();
    const { positionUpdateThrottle } = AUTO_SCROLL_CONFIG;
    const timeSinceLastUpdate = now - lastPositionUpdateTimeRef.current;

    // Throttle position updates
    if (timeSinceLastUpdate < positionUpdateThrottle) {
      return;
    }

    lastPositionUpdateTimeRef.current = now;
    lastClientPositionRef.current = { x: clientX, y: clientY };

    // Invalidate bounds cache when position changes significantly
    // This ensures bounds are recalculated when scrolling
    const cache = cachedScrollElementsRef.current;
    if (cache.horizontal.bounds) {
      cache.horizontal.lastBoundsUpdate = 0; // Force update on next frame
    }
    if (cache.vertical.bounds) {
      cache.vertical.lastBoundsUpdate = 0; // Force update on next frame
    }
  }, []);

  /**
   * Register the horizontal scroll container
   */
  const setHorizontalScrollRef = useCallback((el) => {
    // For Animated.ScrollView, we need to get the underlying DOM node
    if (el && el._component) {
      horizontalScrollRef.current = el._component;
    } else if (el) {
      horizontalScrollRef.current = el;
    } else {
      horizontalScrollRef.current = null;
    }
  }, []);

  /**
   * Register the vertical scroll container
   */
  const setVerticalScrollRef = useCallback((el) => {
    verticalScrollRef.current = el;
  }, []);

  /**
   * Set the active drag item (exercise data)
   * Called when drag starts
   */
  const setActiveDragItem = useCallback((item) => {
    activeDragItemRef.current = item;
  }, []);

  /**
   * Get the active drag item
   */
  const getActiveDragItem = useCallback(() => {
    return activeDragItemRef.current;
  }, []);

  /**
   * Clear the active drag item
   * Called when drag ends
   */
  const clearActiveDragItem = useCallback(() => {
    activeDragItemRef.current = null;
  }, []);

  /**
   * Check if currently dragging
   */
  const isDragging = useCallback(() => {
    return isDraggingRef.current;
  }, []);

  /**
   * Cleanup effect: Ensure all listeners are removed on unmount
   * This prevents memory leaks and ensures listeners are never active when not dragging
   */
  useEffect(() => {
    return () => {
      // Stop auto-scroll and remove all listeners on unmount
      isDraggingRef.current = false;
      stopPointerTracking();
      
      // Cancel any pending animation frames
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
        scrollAnimationRef.current = null;
      }
      
      // Reset scroll speeds
      currentScrollSpeedsRef.current = { horizontal: 0, vertical: 0 };
    };
  }, [stopPointerTracking]);

  const contextValue = {
    // Auto-scroll controls
    startAutoScroll,
    stopAutoScroll,
    updateDragPosition,

    // Scroll container refs
    setHorizontalScrollRef,
    setVerticalScrollRef,
    horizontalScrollRef,
    verticalScrollRef,

    // Active drag item management
    setActiveDragItem,
    getActiveDragItem,
    clearActiveDragItem,
    isDragging,
  };

  return (
    <ExerciseDragContext.Provider value={contextValue}>
      {children}
    </ExerciseDragContext.Provider>
  );
};

export const useExerciseDrag = () => {
  const context = useContext(ExerciseDragContext);
  if (!context) {
    throw new Error(
      "useExerciseDrag must be used within an ExerciseDragProvider",
    );
  }
  return context;
};

export default ExerciseDragContext;
